import { useState } from "react";
import { CATEGORY_STYLE } from "../utils/fieldMaps";
import { fmtMoney, fmtPct } from "../parsers/xmlParser";

import SummaryTab       from "./tabs/SummaryTab";
import FinancialTab     from "./tabs/FinancialTab";
import BreakdownTab     from "./tabs/BreakdownTab";
import CoverageTab      from "./tabs/CoverageTab";
import DepositsTab      from "./tabs/DepositsTab";
import MasluliMTab      from "./tabs/MasluliMTab";
import RetirementTab    from "./tabs/RetirementTab";
import AgentTab         from "./tabs/AgentTab";
import EmployerTab      from "./tabs/EmployerTab";
import BeneficiariesTab from "./tabs/BeneficiariesTab";
import LoanTab          from "./tabs/LoanTab";
import MigdalPolicyTab    from "./tabs/MigdalPolicyTab";
import KisuimMigdalTab    from "./tabs/KisuimMigdalTab";
import ManagedPortfolioTab from "./tabs/ManagedPortfolioTab";

export default function ExpandedPanel({ fund, computed }) {
  const cs = CATEGORY_STYLE[fund.cat] || CATEGORY_STYLE["ביטוח מנהלים"];
  const isMigdalPolicy = fund.cat === "פרט" && fund.kisuimMigdal?.length > 0;
  const isManaged = fund.cat === "תיק מנוהל" || !!fund.managedPortfolio
    || String(fund.id || "").startsWith("MP_") || String(fund.companyCode || "").startsWith("MP_");
  const isHealthInsurance = fund.monthlyPremiumTotal != null;
  const [tab, setTab] = useState(
    isMigdalPolicy ? "migdalPolicy" : isManaged ? "maslulim" : isHealthInsurance ? "maslulim" : "summary"
  );
  const hasLoan = !!fund.loan;
  const hideCoverageCats = new Set(["קופת גמל", "קרן השתלמות", "קופת גמל להשקעה", "חיסכון לכל ילד", "חיסכון"]);
  const hideCoverage = hideCoverageCats.has(fund.cat);

  const TABS = isMigdalPolicy
    ? [
        { id: "migdalPolicy", label: "פרטי פוליסה" },
        { id: "kisuimMigdal", label: `כיסויים (${fund.kisuimMigdal.length})` },
      ]
    : isManaged
    ? [
        { id: "maslulim", label: "הרכב נכסים" },
        { id: "managed",  label: "פרטי תיק" },
      ]
    : isHealthInsurance
    ? [
        { id: "maslulim", label: `כיסוי ביטוח (${fund.maslulim?.length || 0})` },
      ]
    : [
    { id: "summary",       label: "סיכום" },
    { id: "financial",     label: "כספי" },
    { id: "breakdown",     label: "פירוט שכבות" },
    ...(!hideCoverage ? [{ id: "coverage", label: "כיסויים" }] : []),
    { id: "deposits",      label: "הפקדות" },
    { id: "retirement",    label: "פרישה" },
    { id: "beneficiaries", label: "מוטבים" },
    ...(fund.agent    ? [{ id: "agent",    label: "סוכן/מיופה כוח" }] : []),
    ...(fund.employer ? [{ id: "employer", label: "מעסיק" }] : []),
    ...(fund.maslulim?.length > 0 ? [{ id: "maslulim", label: "מסלולי השקעה" }] : []),
    ...(hasLoan       ? [{ id: "loan",     label: "🏦 הלוואה" }] : []),
  ];

  const m = fund._month; // present when a past month is selected (אחזקות month filter)

  return (
    <div style={{ borderTop: `1.5px dashed ${cs.border}`, background: cs.bg }}>
      {m && <MonthBanner m={m} cs={cs} />}
      <div style={{ display: "flex", gap: 0, background: "#f8fafc", borderBottom: "1px solid #e2e8f0", overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "8px 14px", fontSize: 12, fontWeight: tab === t.id ? 700 : 400,
            color: tab === t.id ? cs.accent : "#64748b",
            background: tab === t.id ? "white" : "transparent",
            border: "none", borderBottom: tab === t.id ? `2px solid ${cs.accent}` : "2px solid transparent",
            cursor: "pointer", whiteSpace: "nowrap",
          }}>{t.label}</button>
        ))}
      </div>
      <div style={{ padding: "16px 16px 20px" }}>
        {tab === "migdalPolicy"  && <MigdalPolicyTab    fund={fund} cs={cs} />}
        {tab === "kisuimMigdal"  && <KisuimMigdalTab    fund={fund} cs={cs} />}
        {tab === "managed"       && <ManagedPortfolioTab fund={fund} cs={cs} />}
        {tab === "summary"       && <SummaryTab       fund={fund} cs={cs} />}
        {tab === "financial"     && <FinancialTab     fund={fund} cs={cs} redeem={computed.redeem} hk={computed.hk} sumPitzuyim={computed.sumPitzuyim} sumTagmulim={computed.sumTagmulim} />}
        {tab === "breakdown"     && <BreakdownTab     fund={fund} cs={cs} />}
        {tab === "coverage"      && <CoverageTab      fund={fund} cs={cs} />}
        {tab === "deposits"      && <DepositsTab      fund={fund} cs={cs} />}
        {tab === "maslulim"      && <MasluliMTab      fund={fund} cs={cs} />}
        {tab === "retirement"    && <RetirementTab    fund={fund} cs={cs} />}
        {tab === "agent"         && <AgentTab         fund={fund} cs={cs} />}
        {tab === "employer"      && <EmployerTab      fund={fund} cs={cs} />}
        {tab === "beneficiaries" && <BeneficiariesTab fund={fund} cs={cs} />}
        {tab === "loan"          && <LoanTab          fund={fund} cs={cs} />}
      </div>
    </div>
  );
}

// Snapshot of this fund for the month chosen in the holdings month filter.
// Other tabs below still reflect the latest import (snapshots carry balances/flows,
// not the full XML), so this banner makes the selected month explicit.
function MonthBanner({ m, cs }) {
  const pctColor = m.ret == null ? "#64748b" : m.ret >= 0 ? "#059669" : "#dc2626";
  const Cell = ({ label, value, color }) => (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 90 }}>
      <span style={{ fontSize: 10, color: "#92400e", opacity: 0.8 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: color || "#1e293b" }}>{value}</span>
    </div>
  );
  return (
    <div style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a", padding: "10px 16px" }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Cell label={`נכון לחודש ${m.period.split("-").reverse().join("/")}`} value={fmtMoney(m.balance)} color={cs.accent} />
        {m.deposits !== undefined && <Cell label="הפקדות" value={m.deposits ? fmtMoney(m.deposits) : "—"} />}
        {m.withdrawals !== undefined && <Cell label="משיכות/ניוד" value={m.withdrawals ? fmtMoney(m.withdrawals) : "—"} />}
        {m.ret !== undefined && <Cell label="תשואה בחודש" value={m.ret == null ? "—" : (fmtPct(m.ret * 100) || "0.00%")} color={pctColor} />}
        {(m.dmeiNihulTzv != null || m.dmeiNihulHaf != null) && (
          <Cell
            label="דמי ניהול (צבירה/הפקדה)"
            value={`${m.dmeiNihulTzv != null ? fmtPct(m.dmeiNihulTzv) : "—"} / ${m.dmeiNihulHaf != null ? fmtPct(m.dmeiNihulHaf) : "—"}`}
          />
        )}
      </div>
      {m.maslulim?.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {m.maslulim.map((ms, i) => (
            <span key={i} style={{
              fontSize: 11, background: "white", border: "1px solid #fde68a",
              borderRadius: 6, padding: "3px 8px", color: "#334155",
            }}>
              {ms.name}: <b>{fmtMoney(ms.balance)}</b>
              {ms.tsua != null && <span style={{ color: ms.tsua >= 0 ? "#059669" : "#dc2626" }}> · {fmtPct(ms.tsua)}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
